import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  selectActiveOrganizationId,
  selectIsSuperAdmin,
  selectMemberships,
} from "../../redux/authSlice";
import {
  deleteStageTemplate,
  fetchAuthKeyTemplates,
  listOrgIntegrations,
  listStageTemplates,
  listTenantOrganizations,
  upsertOrgIntegration,
  testOrgSmtp,
  upsertStageTemplate,
} from "../../services/organizationService";
import { getStage } from "../MetaData/components/MetaList/components/LeadStageData/LeadStageListService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import engageLogo from "../../assets/icons/engage-logo.png";
import {
  connectAllMetaPages,
  disconnectMeta,
  getMetaStatus,
  listMetaForms,
  mapMetaForm,
  openMetaOAuthPopup,
  syncMetaCampaigns,
} from "../../services/metaLeadAdsService";
import { getAllForms } from "../AllLeads/components/ShowAll/ShowAllService";
import urls from "../../global/constants/UrlConstants";
import history from "../../utils/history";

function normalizeTemplateList(payload: any): any[] {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.templates)) return payload.templates;
  if (Array.isArray(payload?.data?.templates)) return payload.data.templates;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
}

function templateLabel(t: any) {
  const wid = t.wid || t.template_id || t.id || "";
  const name = t.name || t.temp_name || t.template_name || "";
  return name ? `${name} (${wid})` : String(wid);
}

function stageDisplayName(s: any): string {
  return String(s?.stage_Name || s?.stage_name || "").trim();
}

/** Flatten Lead Management tree (parent + sub stages) into selectable rows */
function flattenLeadStages(tree: any[]): { id: string; label: string; isParent: boolean }[] {
  const result: { id: string; label: string; isParent: boolean }[] = [];
  const walk = (nodes: any[], isParentLevel: boolean) => {
    (nodes || []).forEach((stage) => {
      const label = stageDisplayName(stage);
      if (!label) return;
      // Use stage_Name as key — lead.stage stores the name string
      result.push({
        id: label,
        label: isParentLevel ? label : `↳ ${label}`,
        isParent: isParentLevel,
      });
      if (Array.isArray(stage.sub_Stage_name) && stage.sub_Stage_name.length) {
        walk(stage.sub_Stage_name, false);
      }
    });
  };
  walk(Array.isArray(tree) ? tree : [], true);
  // Dedupe by id (name)
  const seen = new Set<string>();
  return result.filter((s) => {
    if (seen.has(s.id)) return false;
    seen.add(s.id);
    return true;
  });
}

function looksMasked(value: string) {
  return !value || value.includes("*");
}

function isNewSecretValue(value: string | undefined | null) {
  const s = String(value || "").trim();
  return s.length > 0 && !s.includes("*");
}

const OrgIntegrationsPage: React.FC = () => {
  const location = useLocation();
  const queryOrg = new URLSearchParams(location.search).get("org");
  const activeOrganizationId = useSelector(selectActiveOrganizationId);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const memberships = useSelector(selectMemberships);

  const [orgs, setOrgs] = useState<any[]>([]);
  const [organizationId, setOrganizationId] = useState(
    queryOrg ||
      (activeOrganizationId && activeOrganizationId !== "all"
        ? activeOrganizationId
        : "") ||
      ""
  );
  const [authkey, setAuthkey] = useState("");
  const [authkeyConfigured, setAuthkeyConfigured] = useState(false);
  const [shareFrom, setShareFrom] = useState("");
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [otpWid, setOtpWid] = useState("");
  const [assignedWid, setAssignedWid] = useState("");
  const [assignedAdminWhatsapp, setAssignedAdminWhatsapp] =
    useState("8929993099");
  const [savingAssigned, setSavingAssigned] = useState(false);
  const [gadsCustomerId, setGadsCustomerId] = useState("");
  const [gadsLoginCustomerId, setGadsLoginCustomerId] = useState("");
  const [gadsDeveloperToken, setGadsDeveloperToken] = useState("");
  const [gadsDeveloperTokenConfigured, setGadsDeveloperTokenConfigured] =
    useState(false);
  const [gadsRefreshToken, setGadsRefreshToken] = useState("");
  const [gadsRefreshTokenConfigured, setGadsRefreshTokenConfigured] =
    useState(false);
  const [gadsQualifiedId, setGadsQualifiedId] = useState("");
  const [gadsSiteVisitId, setGadsSiteVisitId] = useState("");
  const [gadsVisitPlannedId, setGadsVisitPlannedId] = useState("");
  const [savingGads, setSavingGads] = useState(false);
  const [leadStages, setLeadStages] = useState<any[]>([]);
  const [savedStageTemplates, setSavedStageTemplates] = useState<any[]>([]);
  /** stageName -> templateWid ("" = none) */
  const [stageTemplateMap, setStageTemplateMap] = useState<
    Record<string, string>
  >({});
  const [savingStage, setSavingStage] = useState<string | null>(null);

  const [housingSecret, setHousingSecret] = useState("");
  const [housingSecretConfigured, setHousingSecretConfigured] = useState(false);
  const [housingId, setHousingId] = useState("");
  const [acresUser, setAcresUser] = useState("");
  const [acresPass, setAcresPass] = useState("");
  const [acresPassConfigured, setAcresPassConfigured] = useState(false);
  const [mbApiKey, setMbApiKey] = useState("");
  const [mbApiKeyConfigured, setMbApiKeyConfigured] = useState(false);
  const [mbPartnerId, setMbPartnerId] = useState("");
  const [easebuzzKey, setEasebuzzKey] = useState("");
  const [easebuzzKeyConfigured, setEasebuzzKeyConfigured] = useState(false);
  const [easebuzzSalt, setEasebuzzSalt] = useState("");
  const [easebuzzSaltConfigured, setEasebuzzSaltConfigured] = useState(false);
  const [easebuzzEnv, setEasebuzzEnv] = useState<"test" | "prod">("test");
  const [easebuzzDefaultAmount, setEasebuzzDefaultAmount] = useState("");
  const [easebuzzAmountOverride, setEasebuzzAmountOverride] = useState(false);

  // Per-org outbound email. When set, payment links and other org mail are
  // sent from this account instead of the shared Yhataw (SES) sender.
  const [smtpEnabled, setSmtpEnabled] = useState(false);
  const [smtpUser, setSmtpUser] = useState("");
  const [smtpPassword, setSmtpPassword] = useState("");
  const [smtpPasswordConfigured, setSmtpPasswordConfigured] = useState(false);
  const [smtpFromName, setSmtpFromName] = useState("");
  /** Address recipients see. Defaults to the login mailbox when left blank. */
  const [smtpFromEmail, setSmtpFromEmail] = useState("");
  const [smtpHost, setSmtpHost] = useState("");
  const [smtpPort, setSmtpPort] = useState("");
  const [smtpReplyTo, setSmtpReplyTo] = useState("");
  const [smtpTestTo, setSmtpTestTo] = useState("");
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState("");

  const [moCompanyId, setMoCompanyId] = useState("");
  const [moXApiKey, setMoXApiKey] = useState("");
  const [moXApiKeyConfigured, setMoXApiKeyConfigured] = useState(false);
  const [moSecretKey, setMoSecretKey] = useState("");
  const [moSecretKeyConfigured, setMoSecretKeyConfigured] = useState(false);
  const [moSecretToken, setMoSecretToken] = useState("");
  const [moSecretTokenConfigured, setMoSecretTokenConfigured] = useState(false);
  const [moCallingToken, setMoCallingToken] = useState("");
  const [moCallingTokenConfigured, setMoCallingTokenConfigured] = useState(false);
  const [moPublicIvrId, setMoPublicIvrId] = useState("");
  const [moCallerId, setMoCallerId] = useState("");
  const [moWebhookSecret, setMoWebhookSecret] = useState("");
  const [moWebhookSecretConfigured, setMoWebhookSecretConfigured] =
    useState(false);

  const [metaConnected, setMetaConnected] = useState(false);
  const [metaBusy, setMetaBusy] = useState(false);
  const [metaConnectedPages, setMetaConnectedPages] = useState<any[]>([]);
  const [metaForms, setMetaForms] = useState<any[]>([]);
  const [crmForms, setCrmForms] = useState<{ id: string; label: string }[]>(
    []
  );
  const [metaWebhook, setMetaWebhook] = useState<{
    callbackUrl?: string;
    verifyToken?: string;
    insertLeadsUrl?: string;
    oauthRedirectUri?: string;
    frontendUrl?: string;
    appDomainsHint?: string[];
    metaLeadgen?: { sample?: any; note?: string };
    thirdParty?: { sample?: any; note?: string };
  }>({});
  const mappedFormsCount = useMemo(
    () => metaForms.filter((f) => f.map_form_id).length,
    [metaForms]
  );

  const orgCrmForms = useMemo(() => {
    if (!organizationId) return [];
    return crmForms.filter((f) => {
      const raw = (f as any).organizationId;
      if (!raw) return false;
      return String(raw) === String(organizationId);
    });
  }, [crmForms, organizationId]);

  const otherOrgs = useMemo(
    () => orgs.filter((o) => String(o._id) !== String(organizationId)),
    [orgs, organizationId]
  );

  const templateOptions = useMemo(
    () => (Array.isArray(templates) ? templates : []),
    [templates]
  );

  const stageRows = useMemo(() => flattenLeadStages(leadStages), [leadStages]);

  const loadOrgs = useCallback(async () => {
    const res = await listTenantOrganizations();
    if (res?.success === 1) {
      let list = res.data || [];
      if (!isSuperAdmin) {
        const allowed = new Set(
          (memberships || []).map((m) => String(m.organizationId))
        );
        list = list.filter((o: any) => allowed.has(String(o._id)));
      }
      setOrgs(list);
      const currentOk = list.some(
        (o: any) => String(o._id) === String(organizationId)
      );
      if (!currentOk && list.length) {
        const preferred =
          queryOrg &&
          list.find((o: any) => String(o._id) === String(queryOrg));
        const fromActive =
          activeOrganizationId &&
          activeOrganizationId !== "all" &&
          list.find(
            (o: any) => String(o._id) === String(activeOrganizationId)
          );
        setOrganizationId(
          String((preferred || fromActive || list[0])._id)
        );
      }
    }
  }, [
    isSuperAdmin,
    memberships,
    organizationId,
    queryOrg,
    activeOrganizationId,
  ]);

  useEffect(() => {
    loadOrgs();
  }, [loadOrgs]);

  useEffect(() => {
    (async () => {
      try {
        // Lead Management → Stage tree (/getLeadStage), NOT lead_status
        const res = await getStage();
        const rows = res?.data || res?.message?.data || res || [];
        const list = Array.isArray(rows)
          ? rows
          : Array.isArray(rows?.data)
            ? rows.data
            : [];
        setLeadStages(list);
      } catch (_) {
        setLeadStages([]);
      }
    })();
  }, []);

  const refreshOrgConfig = useCallback(async () => {
    if (!organizationId) return;
    const [ints, stages] = await Promise.all([
      listOrgIntegrations(organizationId),
      listStageTemplates(organizationId),
    ]);
    if (ints?.success === 1) {
      const gads = (ints.data || []).find((i: any) => i.type === "GOOGLE_ADS");
      if (gads?.credentials) {
        const gc = gads.credentials;
        setGadsCustomerId(String(gc.customerId || ""));
        setGadsLoginCustomerId(String(gc.loginCustomerId || ""));
        setGadsQualifiedId(String(gc.conversionQualifiedId || ""));
        setGadsSiteVisitId(String(gc.conversionSiteVisitId || ""));
        setGadsVisitPlannedId(String(gc.conversionVisitPlannedId || ""));
        if (gc.developerToken) {
          setGadsDeveloperToken(String(gc.developerToken));
          setGadsDeveloperTokenConfigured(true);
        } else {
          setGadsDeveloperToken("");
          setGadsDeveloperTokenConfigured(false);
        }
        if (gc.refreshToken) {
          setGadsRefreshToken(String(gc.refreshToken));
          setGadsRefreshTokenConfigured(true);
        } else {
          setGadsRefreshToken("");
          setGadsRefreshTokenConfigured(false);
        }
      } else {
        setGadsCustomerId("");
        setGadsLoginCustomerId("");
        setGadsQualifiedId("");
        setGadsSiteVisitId("");
        setGadsVisitPlannedId("");
        setGadsDeveloperToken("");
        setGadsDeveloperTokenConfigured(false);
        setGadsRefreshToken("");
        setGadsRefreshTokenConfigured(false);
      }

      const ak = (ints.data || []).find((i: any) => i.type === "AUTHKEY");
      setShareFrom(
        ak?.sharedFromOrganizationId
          ? String(ak.sharedFromOrganizationId)
          : ""
      );
      if (ak?.credentials?.authkey) {
        setAuthkey(String(ak.credentials.authkey));
        setAuthkeyConfigured(true);
      } else {
        setAuthkey("");
        setAuthkeyConfigured(!!ak);
      }

      const housing = (ints.data || []).find((i: any) => i.type === "HOUSING");
      if (housing?.credentials) {
        setHousingId(housing.credentials.id || "");
        if (housing.credentials.secretKey) {
          setHousingSecret(String(housing.credentials.secretKey));
          setHousingSecretConfigured(true);
        } else {
          setHousingSecret("");
          setHousingSecretConfigured(false);
        }
      } else {
        setHousingId("");
        setHousingSecret("");
        setHousingSecretConfigured(false);
      }

      const acres = (ints.data || []).find(
        (i: any) => i.type === "NINETY_NINE_ACRES"
      );
      if (acres?.credentials) {
        setAcresUser(acres.credentials.user_name || "");
        if (acres.credentials.pswd) {
          setAcresPass(String(acres.credentials.pswd));
          setAcresPassConfigured(true);
        } else {
          setAcresPass("");
          setAcresPassConfigured(false);
        }
      } else {
        setAcresUser("");
        setAcresPass("");
        setAcresPassConfigured(false);
      }

      const mb = (ints.data || []).find((i: any) => i.type === "MAGICBRICKS");
      if (mb?.credentials) {
        setMbPartnerId(mb.credentials.partnerId || mb.credentials.id || "");
        if (mb.credentials.apiKey || mb.credentials.secretKey) {
          setMbApiKey(
            String(mb.credentials.apiKey || mb.credentials.secretKey)
          );
          setMbApiKeyConfigured(true);
        } else {
          setMbApiKey("");
          setMbApiKeyConfigured(false);
        }
      } else {
        setMbPartnerId("");
        setMbApiKey("");
        setMbApiKeyConfigured(false);
      }

      const easebuzz = (ints.data || []).find((i: any) => i.type === "EASEBUZZ");
      if (easebuzz?.credentials) {
        if (easebuzz.credentials.key) {
          setEasebuzzKey(String(easebuzz.credentials.key));
          setEasebuzzKeyConfigured(true);
        } else {
          setEasebuzzKey("");
          setEasebuzzKeyConfigured(false);
        }
        if (easebuzz.credentials.salt) {
          setEasebuzzSalt(String(easebuzz.credentials.salt));
          setEasebuzzSaltConfigured(true);
        } else {
          setEasebuzzSalt("");
          setEasebuzzSaltConfigured(false);
        }
        setEasebuzzEnv(
          String(easebuzz.credentials.env).toLowerCase() === "prod"
            ? "prod"
            : "test"
        );
        setEasebuzzDefaultAmount(
          easebuzz.credentials.defaultAmount
            ? String(easebuzz.credentials.defaultAmount)
            : ""
        );
        setEasebuzzAmountOverride(!!easebuzz.credentials.defaultAmountEnabled);
      } else {
        setEasebuzzKey("");
        setEasebuzzKeyConfigured(false);
        setEasebuzzSalt("");
        setEasebuzzSaltConfigured(false);
        setEasebuzzEnv("test");
        setEasebuzzDefaultAmount("");
        setEasebuzzAmountOverride(false);
      }

      const smtp = (ints.data || []).find((i: any) => i.type === "SMTP");
      const smtpCreds = smtp?.credentials || {};
      setSmtpUser(String(smtpCreds.username || ""));
      setSmtpFromName(String(smtpCreds.fromName || ""));
      setSmtpFromEmail(String(smtpCreds.fromEmail || ""));
      setSmtpHost(String(smtpCreds.host || ""));
      setSmtpPort(String(smtpCreds.port || ""));
      setSmtpReplyTo(String(smtpCreds.replyTo || ""));
      setSmtpPassword(String(smtpCreds.password || ""));
      setSmtpPasswordConfigured(!!smtpCreds.password);
      setSmtpEnabled(!!smtp && smtp.status === "active");
      setSmtpTestResult("");

      const myop = (ints.data || []).find((i: any) => i.type === "MYOPERATOR");
      if (myop?.credentials) {
        const c = myop.credentials;
        setMoCompanyId(c.companyId || "");
        setMoPublicIvrId(c.publicIvrId || "");
        setMoCallerId(c.callerId || "");
        if (c.xApiKey) {
          setMoXApiKey(String(c.xApiKey));
          setMoXApiKeyConfigured(true);
        } else {
          setMoXApiKey("");
          setMoXApiKeyConfigured(false);
        }
        if (c.secretKey) {
          setMoSecretKey(String(c.secretKey));
          setMoSecretKeyConfigured(true);
        } else {
          setMoSecretKey("");
          setMoSecretKeyConfigured(false);
        }
        if (c.secretToken) {
          setMoSecretToken(String(c.secretToken));
          setMoSecretTokenConfigured(true);
        } else {
          setMoSecretToken("");
          setMoSecretTokenConfigured(false);
        }
        if (c.callingToken) {
          setMoCallingToken(String(c.callingToken));
          setMoCallingTokenConfigured(true);
        } else {
          setMoCallingToken("");
          setMoCallingTokenConfigured(false);
        }
        if (c.webhookSecret) {
          setMoWebhookSecret(String(c.webhookSecret));
          setMoWebhookSecretConfigured(true);
        } else {
          setMoWebhookSecret("");
          setMoWebhookSecretConfigured(false);
        }
      } else {
        setMoCompanyId("");
        setMoXApiKey("");
        setMoXApiKeyConfigured(false);
        setMoSecretKey("");
        setMoSecretKeyConfigured(false);
        setMoSecretToken("");
        setMoSecretTokenConfigured(false);
        setMoCallingToken("");
        setMoCallingTokenConfigured(false);
        setMoPublicIvrId("");
        setMoCallerId("");
        setMoWebhookSecret("");
        setMoWebhookSecretConfigured(false);
      }
    }
    if (stages?.success === 1) {
      const all = stages.data || [];
      const stageTriggers = all.filter(
        (s: any) => s.purpose === "stage_trigger"
      );
      setSavedStageTemplates(stageTriggers);
      const map: Record<string, string> = {};
      stageTriggers.forEach((s: any) => {
        const key = String(s.stageName || s.stageId || "");
        if (key && s.templateWid) map[key] = String(s.templateWid);
      });
      setStageTemplateMap(map);
      const otp = all.find((s: any) => s.purpose === "otp");
      if (otp?.templateWid) setOtpWid(String(otp.templateWid));
      else setOtpWid("");
      const assigned = all.find((s: any) => s.purpose === "lead_assigned");
      if (assigned?.templateWid) setAssignedWid(String(assigned.templateWid));
      else setAssignedWid("");
      const adminWa = String(
        assigned?.variables?.adminWhatsapp || ""
      ).replace(/\D/g, "");
      setAssignedAdminWhatsapp(adminWa.slice(-10) || "8929993099");
    }
  }, [organizationId]);

  useEffect(() => {
    refreshOrgConfig();
    setTemplates([]);
  }, [refreshOrgConfig]);

  const saveAuthKey = async () => {
    if (!organizationId) return;
    const body: any = { type: "AUTHKEY", status: "active" };
    if (shareFrom) {
      body.sharedFromOrganizationId = shareFrom;
      body.credentials = {};
    } else {
      if (!authkey.trim() && !authkeyConfigured) {
        openErrorNotification("Paste Engage key or choose share-from org");
        return;
      }
      // If a new secret was entered, send it; otherwise omit so backend retains existing secret
      const creds: Record<string, string> = {};
      if (isNewSecretValue(authkey)) {
        creds.authkey = authkey.trim();
      }
      body.credentials = creds;
      body.sharedFromOrganizationId = null;
    }
    const res = await upsertOrgIntegration(organizationId, body);
    if (res?.success === 1) {
      openSuccessNotification("Engage saved");
      await refreshOrgConfig();
    } else {
      openErrorNotification(res?.message || "Save failed");
    }
  };

  const loadTemplates = async () => {
    if (!organizationId) return;
    setLoadingTemplates(true);
    try {
      const res = await fetchAuthKeyTemplates(organizationId, "whatsapp");
      if (res?.success === 1) {
        const list = normalizeTemplateList(res.data);
        setTemplates(list);
        if (!list.length) {
          openErrorNotification(
            "No templates returned. Check Engage credentials for this organization."
          );
        } else {
          openSuccessNotification(`Loaded ${list.length} templates`);
        }
      } else {
        setTemplates([]);
        openErrorNotification(res?.message || "Failed to fetch templates");
      }
    } catch (e: any) {
      setTemplates([]);
      openErrorNotification(e?.message || "Failed to fetch templates");
    } finally {
      setLoadingTemplates(false);
    }
  };

  const saveOtpTemplate = async () => {
    if (!organizationId) return;
    if (!otpWid) {
      openErrorNotification("Select an OTP template (or leave unset)");
      return;
    }
    const tpl = templateOptions.find(
      (t) => String(t.wid || t.id) === String(otpWid)
    );
    const res = await upsertStageTemplate(organizationId, {
      purpose: "otp",
      channel: "whatsapp",
      templateWid: otpWid,
      templateName: tpl?.name || tpl?.temp_name || "OTP",
    });
    if (res?.success === 1) openSuccessNotification("OTP template saved");
    else openErrorNotification(res?.message || "Failed");
  };

  const saveLeadAssignedTemplate = async () => {
    if (!organizationId) return;
    setSavingAssigned(true);
    try {
      const adminDigits = String(assignedAdminWhatsapp || "")
        .replace(/\D/g, "")
        .slice(-10);
      if (!assignedWid) {
        const res = await upsertStageTemplate(organizationId, {
          purpose: "lead_assigned",
          channel: "whatsapp",
          clear: true,
          templateWid: "",
        });
        if (res?.success === 1) {
          openSuccessNotification("Cleared new-lead assigned template");
          await refreshOrgConfig();
        } else openErrorNotification(res?.message || "Failed");
        return;
      }
      const tpl = templateOptions.find(
        (t) => String(t.wid || t.id) === String(assignedWid)
      );
      const res = await upsertStageTemplate(organizationId, {
        purpose: "lead_assigned",
        channel: "whatsapp",
        templateWid: assignedWid,
        templateName:
          tpl?.name || tpl?.temp_name || "staff_new_lead_assigned",
        variables: {
          adminWhatsapp: adminDigits || "8929993099",
          adminCountryCode: "91",
        },
      });
      if (res?.success === 1) {
        openSuccessNotification(
          "New-lead assigned template saved. Staff + admin will get WhatsApp when a lead is assigned."
        );
        await refreshOrgConfig();
      } else openErrorNotification(res?.message || "Failed");
    } finally {
      setSavingAssigned(false);
    }
  };

  const saveGoogleAds = async () => {
    if (!organizationId) return;
    setSavingGads(true);
    try {
      const creds: Record<string, string> = {
        customerId: gadsCustomerId.trim(),
        loginCustomerId: gadsLoginCustomerId.trim(),
        conversionQualifiedId: gadsQualifiedId.trim(),
        conversionSiteVisitId: gadsSiteVisitId.trim(),
        conversionVisitPlannedId: gadsVisitPlannedId.trim(),
      };
      if (isNewSecretValue(gadsDeveloperToken)) {
        creds.developerToken = gadsDeveloperToken.trim();
      }
      if (isNewSecretValue(gadsRefreshToken)) {
        creds.refreshToken = gadsRefreshToken.trim();
      }
      const res = await upsertOrgIntegration(organizationId, {
        type: "GOOGLE_ADS",
        status: "active",
        credentials: creds,
      });
      if (res?.success === 1) {
        openSuccessNotification("Google Ads offline conversions saved");
        await refreshOrgConfig();
      } else openErrorNotification(res?.message || "Failed");
    } finally {
      setSavingGads(false);
    }
  };

  const findSavedRow = (stageKey: string) =>
    savedStageTemplates.find(
      (s) =>
        String(s.stageName) === stageKey || String(s.stageId) === stageKey
    );

  const saveOneStageTemplate = async (stageKey: string, wid: string) => {
    if (!organizationId || !stageKey) return;
    setSavingStage(stageKey);
    try {
      if (!wid) {
        const existing = findSavedRow(stageKey);
        if (existing?._id) {
          const res = await deleteStageTemplate(
            organizationId,
            String(existing._id),
            stageKey
          );
          if (res?.success === 1) {
            openSuccessNotification(`Cleared template for “${stageKey}”`);
            await refreshOrgConfig();
          } else openErrorNotification(res?.message || "Failed to clear");
        } else {
          // Also try clear via upsert
          const res = await upsertStageTemplate(organizationId, {
            purpose: "stage_trigger",
            channel: "whatsapp",
            stageId: stageKey,
            stageName: stageKey,
            clear: true,
            templateWid: "",
          });
          if (res?.success === 1) {
            openSuccessNotification(`No template for “${stageKey}”`);
            await refreshOrgConfig();
          }
        }
        return;
      }

      const tpl = templateOptions.find(
        (t) => String(t.wid || t.id) === String(wid)
      );
      const res = await upsertStageTemplate(organizationId, {
        purpose: "stage_trigger",
        channel: "whatsapp",
        stageId: stageKey,
        stageName: stageKey,
        templateWid: wid,
        templateName: tpl?.name || tpl?.temp_name || "",
      });
      if (res?.success === 1) {
        openSuccessNotification(`Saved template for “${stageKey}”`);
        await refreshOrgConfig();
      } else openErrorNotification(res?.message || "Failed");
    } finally {
      setSavingStage(null);
    }
  };

  const deleteOneStage = async (stageKey: string) => {
    if (!organizationId) return;
    const existing = findSavedRow(stageKey);
    setSavingStage(stageKey);
    try {
      if (existing?._id) {
        const res = await deleteStageTemplate(
          organizationId,
          String(existing._id),
          stageKey
        );
        if (res?.success !== 1) {
          openErrorNotification(res?.message || "Delete failed");
          return;
        }
      } else {
        await upsertStageTemplate(organizationId, {
          purpose: "stage_trigger",
          stageId: stageKey,
          stageName: stageKey,
          clear: true,
          templateWid: "",
        });
      }
      setStageTemplateMap((prev) => {
        const next = { ...prev };
        delete next[stageKey];
        return next;
      });
      openSuccessNotification(`Removed template for “${stageKey}”`);
      await refreshOrgConfig();
    } finally {
      setSavingStage(null);
    }
  };

  const saveHousing = async () => {
    const creds: Record<string, any> = { id: housingId };
    if (isNewSecretValue(housingSecret)) {
      creds.secretKey = housingSecret.trim();
    }
    const res = await upsertOrgIntegration(organizationId, {
      type: "HOUSING",
      status: "active",
      credentials: creds,
    });
    if (res?.success === 1) {
      openSuccessNotification("Housing credentials saved");
      await refreshOrgConfig();
    } else openErrorNotification(res?.message || "Failed");
  };

  const refreshMeta = useCallback(async () => {
    if (!organizationId) return;
    try {
      const [statusRes, formsRes, crmRes] = await Promise.all([
        getMetaStatus(organizationId),
        listMetaForms(organizationId, undefined, true),
        getAllForms().catch(() => null),
      ]);
      if (statusRes?.success === 1) {
        setMetaConnected(!!statusRes.data?.connected);
        setMetaConnectedPages(statusRes.data?.pages || []);
        setMetaWebhook(statusRes.data?.webhook || {});
      }
      if (formsRes?.success === 1) {
        setMetaForms(Array.isArray(formsRes.data) ? formsRes.data : []);
      }
      if (crmRes?.data) {
        setCrmForms(
          (crmRes.data || [])
            .filter((f: any) => String(f.status) === "1")
            .map((f: any) => ({
              id: String(f._id),
              label: f.form_name || f.name || String(f._id),
              organizationId: f.organizationId,
            }))
        );
      }
    } catch (_) {
      /* ignore */
    }
  }, [organizationId]);

  useEffect(() => {
    refreshMeta();
  }, [refreshMeta]);

  const onRefreshFromMeta = async (silent = false) => {
    if (!organizationId) return;
    setMetaBusy(true);
    try {
      const res = await syncMetaCampaigns(organizationId);
      if (res?.success !== 1) {
        if (!silent) {
          openErrorNotification(res?.message || "Refresh from Meta failed");
        }
        return;
      }
      if (Array.isArray(res.data?.forms)) {
        setMetaForms(res.data.forms);
      } else {
        const formsRes = await listMetaForms(organizationId, undefined, true);
        if (formsRes?.success === 1) {
          setMetaForms(Array.isArray(formsRes.data) ? formsRes.data : []);
        }
      }
      if (!silent) {
        openSuccessNotification(
          res.message ||
            `Loaded ${res.data?.forms?.length || 0} Meta form(s) — map each to a Pulse CRM form`
        );
      }
      await refreshMeta();
    } catch (e: any) {
      if (!silent) {
        openErrorNotification(e?.message || "Refresh from Meta failed");
      }
    } finally {
      setMetaBusy(false);
    }
  };

  const connectMetaOAuth = async () => {
    if (!organizationId || metaConnected) return;
    setMetaBusy(true);
    try {
      await openMetaOAuthPopup(organizationId);
      await refreshMeta();
      const statusRes = await getMetaStatus(organizationId);
      const isConnected =
        statusRes?.success === 1 && !!statusRes.data?.connected;
      if (isConnected) {
        setMetaConnected(true);
        openSuccessNotification(
          "Meta connected — click Refresh from Meta to load all forms"
        );
        await onRefreshFromMeta(true);
      } else {
        openErrorNotification("Meta login was not completed — try again");
      }
    } catch (e: any) {
      openErrorNotification(e?.message || "Meta connect failed");
    } finally {
      setMetaBusy(false);
    }
  };

  const onLinkAllPages = async () => {
    if (!organizationId) return;
    setMetaBusy(true);
    try {
      const res = await connectAllMetaPages(organizationId);
      if (res?.success !== 1) {
        openErrorNotification(res?.message || "Failed to link Pages");
        return;
      }
      openSuccessNotification(
        res.message ||
          "Pages linked for this org only — Pages owned by another org were skipped"
      );
      if (Array.isArray(res.data?.syncWarnings) && res.data.syncWarnings.length) {
        openErrorNotification(res.data.syncWarnings.slice(0, 3).join(" · "));
      }
      if (Array.isArray(res.data?.forms)) {
        setMetaForms(res.data.forms);
      }
      await refreshMeta();
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to link Pages");
    } finally {
      setMetaBusy(false);
    }
  };

  const onMapForm = async (metaFormId: string, crmFormId: string) => {
    if (!organizationId) return;
    setMetaBusy(true);
    try {
      const res = await mapMetaForm(organizationId, {
        metaFormId,
        crmFormId: crmFormId || "",
      });
      if (res?.success !== 1) {
        openErrorNotification(res?.message || "Map failed");
        return;
      }
      openSuccessNotification(
        crmFormId
          ? "Form mapped to CRM form / project"
          : "Form unmapped"
      );
      await refreshMeta();
    } catch (e: any) {
      openErrorNotification(e?.message || "Map failed");
    } finally {
      setMetaBusy(false);
    }
  };

  const onDisconnectMeta = async () => {
    if (!organizationId) return;
    setMetaBusy(true);
    try {
      const res = await disconnectMeta(organizationId);
      if (res?.success !== 1) {
        openErrorNotification(res?.message || "Disconnect failed");
        return;
      }
      openSuccessNotification("Meta disconnected");
      setMetaForms([]);
      await refreshMeta();
    } catch (e: any) {
      openErrorNotification(e?.message || "Disconnect failed");
    } finally {
      setMetaBusy(false);
    }
  };

  const saveAcres = async () => {
    const creds: Record<string, any> = { user_name: acresUser };
    if (isNewSecretValue(acresPass)) {
      creds.pswd = acresPass.trim();
    }
    const res = await upsertOrgIntegration(organizationId, {
      type: "NINETY_NINE_ACRES",
      status: "active",
      credentials: creds,
    });
    if (res?.success === 1) {
      openSuccessNotification("99acres credentials saved");
      await refreshOrgConfig();
    } else openErrorNotification(res?.message || "Failed");
  };

  const saveMagicBricks = async () => {
    const creds: Record<string, any> = { partnerId: mbPartnerId };
    if (isNewSecretValue(mbApiKey)) {
      creds.apiKey = mbApiKey.trim();
    }
    const res = await upsertOrgIntegration(organizationId, {
      type: "MAGICBRICKS",
      status: "active",
      credentials: creds,
    });
    if (res?.success === 1) {
      openSuccessNotification("MagicBricks settings saved");
      await refreshOrgConfig();
    } else openErrorNotification(res?.message || "Failed");
  };

  const generateMagicBricksApiKey = () => {
    const key =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `mb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    setMbApiKey(key);
    setMbApiKeyConfigured(false);
    openSuccessNotification("New API key generated — click Save to store it");
  };

  const saveEasebuzz = async () => {
    if (!organizationId) return;
    if (
      !easebuzzKeyConfigured &&
      !easebuzzKey.trim() &&
      !easebuzzSaltConfigured &&
      !easebuzzSalt.trim()
    ) {
      openErrorNotification("Enter Easebuzz Key and Salt");
      return;
    }
    const amountRaw = easebuzzDefaultAmount.trim();
    const amountNum = Number(amountRaw);
    const amountValid =
      !!amountRaw && Number.isFinite(amountNum) && amountNum > 0;
    if (easebuzzAmountOverride && !amountValid) {
      openErrorNotification(
        "Enter a valid override amount, or turn off Override amount"
      );
      return;
    }
    if (amountRaw && !amountValid) {
      openErrorNotification("Enter a valid override amount, or leave it blank");
      return;
    }
    const creds: Record<string, any> = {
      env: easebuzzEnv,
      // The amount is kept even when the override is off, so testing can be
      // switched back on without retyping it.
      defaultAmount: amountValid ? amountNum : "",
      defaultAmountEnabled: easebuzzAmountOverride,
    };
    if (isNewSecretValue(easebuzzKey)) {
      creds.key = easebuzzKey.trim();
    }
    if (isNewSecretValue(easebuzzSalt)) {
      creds.salt = easebuzzSalt.trim();
    }
    const res = await upsertOrgIntegration(organizationId, {
      type: "EASEBUZZ",
      status: "active",
      credentials: creds,
    });
    if (res?.success === 1) {
      openSuccessNotification(`Payment gateway credentials saved (${easebuzzEnv.toUpperCase()} mode)`);
      await refreshOrgConfig();
    } else openErrorNotification(res?.message || "Failed");
  };

  /** Shape the SMTP credential payload, preserving a masked saved password. */
  const buildSmtpCreds = () => {
    const creds: Record<string, any> = {
      username: smtpUser.trim(),
      fromName: smtpFromName.trim(),
      fromEmail: smtpFromEmail.trim(),
      host: smtpHost.trim(),
      port: smtpPort.trim(),
      replyTo: smtpReplyTo.trim(),
    };
    if (isNewSecretValue(smtpPassword)) {
      creds.password = smtpPassword.trim();
    }
    return creds;
  };

  const saveSmtp = async () => {
    if (!organizationId) return;
    if (!smtpUser.trim()) {
      openErrorNotification("Enter the sending email address");
      return;
    }
    if (!smtpPasswordConfigured && !isNewSecretValue(smtpPassword)) {
      openErrorNotification("Enter the app password for this mailbox");
      return;
    }
    const res = await upsertOrgIntegration(organizationId, {
      type: "SMTP",
      status: smtpEnabled ? "active" : "disconnected",
      credentials: buildSmtpCreds(),
    });
    if (res?.success === 1) {
      openSuccessNotification(
        smtpEnabled
          ? `Emails for this organization will now be sent from ${smtpUser.trim()}`
          : "Organization email saved but disabled — mail will use the default sender"
      );
      await refreshOrgConfig();
    } else openErrorNotification(res?.message || "Failed");
  };

  /**
   * Verify before trusting it. A wrong app password otherwise only surfaces
   * when a customer never receives their payment link.
   */
  const testSmtp = async () => {
    if (!organizationId) return;
    setSmtpTesting(true);
    setSmtpTestResult("");
    try {
      const res = await testOrgSmtp(organizationId, {
        credentials: buildSmtpCreds(),
        testTo: smtpTestTo.trim(),
      });
      if (res?.success === 1) {
        setSmtpTestResult(res.message || "Connection successful");
        openSuccessNotification(res.message || "Connection successful");
      } else {
        setSmtpTestResult(res?.message || "Connection failed");
        openErrorNotification(res?.message || "Connection failed");
      }
    } catch (err: any) {
      setSmtpTestResult(err?.message || "Connection failed");
      openErrorNotification(err?.message || "Connection failed");
    } finally {
      setSmtpTesting(false);
    }
  };

  const saveMyOperator = async () => {
    if (!organizationId) return;
    if (!moCompanyId.trim()) {
      openErrorNotification("Company ID is required");
      return;
    }
    if (!moXApiKeyConfigured && !moXApiKey.trim()) {
      openErrorNotification("x-api-key is required");
      return;
    }
    const authToken = moCallingToken.trim() || moSecretToken.trim();
    if (
      !moCallingTokenConfigured &&
      !moSecretTokenConfigured &&
      !authToken
    ) {
      openErrorNotification(
        "Paste Manage → API integration → Public API token"
      );
      return;
    }
    if (!moPublicIvrId.trim() || !moCallerId.trim()) {
      openErrorNotification(
        "Public IVR ID (Call → Campaign) and Caller ID (virtual number) are required"
      );
      return;
    }
    const creds: Record<string, any> = {
      companyId: moCompanyId.trim(),
      publicIvrId: moPublicIvrId.trim(),
      callerId: moCallerId.trim().replace(/\D/g, ""),
    };
    if (isNewSecretValue(moXApiKey)) creds.xApiKey = moXApiKey.trim();
    if (isNewSecretValue(moSecretKey)) creds.secretKey = moSecretKey.trim();
    const tokenVal = moCallingToken.trim() || moSecretToken.trim();
    if (isNewSecretValue(tokenVal)) {
      creds.callingToken = tokenVal;
      creds.secretToken = tokenVal;
    }
    if (isNewSecretValue(moWebhookSecret)) creds.webhookSecret = moWebhookSecret.trim();

    const res = await upsertOrgIntegration(organizationId, {
      type: "MYOPERATOR",
      status: "active",
      credentials: creds,
    });
    if (res?.success === 1) {
      openSuccessNotification("MyOperator IVR credentials saved");
      await refreshOrgConfig();
    } else openErrorNotification(res?.message || "Failed");
  };

  const myOperatorWebhookBase = (
    process.env.REACT_APP_PUBLIC_API_URL ||
    process.env.REACT_APP_API_URL ||
    urls.url_prod ||
    ""
  )
    .trim()
    .replace(/\/$/, "");
  // MyOperator Webhooks v2 reject http:// — always prefer public HTTPS API
  const myOperatorHttpsBase = myOperatorWebhookBase.startsWith("https://")
    ? myOperatorWebhookBase
    : "https://api.yhataw.com";
  const myOperatorWebhookV2Url = `${myOperatorHttpsBase}/myoperator/webhook/v2`;
  const myOperatorAfterCallUrl = `${myOperatorHttpsBase}/afterCall/webhook`;
  const magicBricksApiBase = String(urls.url_prod || "https://api.yhataw.com").replace(
    /\/$/,
    ""
  );
  const magicBricksWebhookUrl = `${magicBricksApiBase}/api/v1/integration/MagicBricks`;
  const magicBricksLegacyWebhookUrl = `${magicBricksApiBase}/getMagicBricksLeads`;

  const selectedOrgName = useMemo(() => {
    const org = orgs.find((o) => String(o._id) === String(organizationId));
    return String(org?.name || org?.brandName || "Organization").trim();
  }, [orgs, organizationId]);

  const magicBricksApiKeyDisplay = mbApiKeyConfigured
    ? mbApiKey
    : mbApiKey.trim() || "<generate and save API key above>";

  const magicBricksClientId = mbPartnerId.trim() || String(organizationId || "");

  const magicBricksParamRows = useMemo(
    () => [
      ["name", "Yes", "Himanshu Kumar", "Lead full name"],
      ["mobile", "Yes", "9988776655", "10-digit mobile (countryCode optional, default 91)"],
      ["project", "Yes", "Sapphire 92", "Project name — auto-maps to CRM if name matches"],
      ["project_id", "No", "12345", "MagicBricks project id → portal uid MB_<id>"],
      ["email", "No", "lead@example.com", "Lead email"],
      ["remark", "No", "Site visit requested", "Free-text — stored on lead"],
      ["city", "No", "Jaipur", "City"],
      ["state", "No", "Rajasthan", "State"],
      ["location", "No", "GK Colony", "Locality"],
      ["budget", "No", "5403192", "Budget"],
      ["notes", "No", "comments", "Comments / notes"],
      ["property", "No", "Villa", "Property type hint"],
      ["propertyType", "No", "Flat", "Flat / Villa etc."],
      ["bhkType", "No", "3 BHK", "BHK type"],
      ["additionalProperties", "No", '{"EnquiredFor":"Buy","NoOfBHK":"3"}', "JSON object"],
    ],
    []
  );

  const magicBricksSuccessResponse = `{
  "success": 1,
  "message": "Data Inserted successfully — map once in Aggregator API",
  "project_uid": "MB_12345",
  "portal": "MAGICBRICKS",
  "mappingStatus": "0",
  "linkedCrmProjectId": null,
  "crmMatchType": null,
  "autoPromoted": false,
  "organizationId": "${organizationId || "<org-id>"}"
}`;

  const magicBricksErrorResponses = `401 — Invalid API key:
{ "success": 0, "message": "Invalid API-Key for MagicBricks integration" }

400 — Missing required fields:
{ "success": 0, "message": "Lead name and mobile/phone are required" }`;

  const magicBricksSampleCurl = `curl --location --request POST '${magicBricksWebhookUrl}' \\
  -H 'API-Key: ${magicBricksApiKeyDisplay}' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "name": "Test Lead",
    "mobile": "9988776655",
    "project": "Sapphire 92",
    "project_id": "12345",
    "remark": "MagicBricks test push"
  }'`;

  const magicBricksIntegrationSheet = useMemo(() => {
    const paramLines = magicBricksParamRows
      .map(
        ([field, req, sample, desc]) =>
          `  • ${field} (${req}): e.g. ${sample} — ${desc}`
      )
      .join("\n");

    return `MAGICBRICKS → YHATAW PULSE CRM — PUSH INTEGRATION SHEET
Organization: ${selectedOrgName}
Generated for: MagicBricks integration team (Kapil Arora / portal onboarding)

CRM Name
  YHATAW Pulse CRM (${selectedOrgName})

Push Integration Type
  Push — Real-time webhook (HTTP POST, JSON body)

Authentication Type
  API Key (HTTP request header)

API Key / Client_Key
  ${magicBricksApiKeyDisplay}
  Header name: API-Key
  Example: API-Key: ${magicBricksApiKeyDisplay}

Client_ID (Partner / Builder id)
  ${magicBricksClientId || "<set Partner id in Integrations or use organization id>"}

Working Endpoint URL (HTTPS POST, open/public)
  ${magicBricksWebhookUrl}

Complete working endpoint (URL + auth + sample body)
  URL: POST ${magicBricksWebhookUrl}
  Headers:
    API-Key: ${magicBricksApiKeyDisplay}
    Content-Type: application/json
  Body (JSON):
{
  "name": "Test Lead",
  "mobile": "9988776655",
  "project": "Sapphire 92",
  "project_id": "12345",
  "remark": "MagicBricks test push"
}

Parameters to pass (field / required / sample):
${paramLines}

Response on successful lead sharing (HTTP 200):
${magicBricksSuccessResponse}

Response on failure:
${magicBricksErrorResponses}

Legacy endpoint (optional, API-Key optional):
  POST ${magicBricksLegacyWebhookUrl}

Integration manual — steps for MagicBricks team
  1. Use Push integration (not pull). YHATAW does not fetch leads from MagicBricks.
  2. Configure POST URL: ${magicBricksWebhookUrl}
  3. Send header API-Key with the key above (unique per builder org).
  4. POST JSON with at minimum: name, mobile, project (project_id recommended).
  5. On success you receive success: 1 — lead appears in YHATAW Aggregator API as MB_<project_id>.
  6. Builder maps project once in Pulse → Aggregator API → then leads flow to All Leads.

Support: Pulse CRM → Organization Integrations → MagicBricks (per-org keys).`;
  }, [
    selectedOrgName,
    magicBricksApiKeyDisplay,
    magicBricksClientId,
    magicBricksWebhookUrl,
    magicBricksLegacyWebhookUrl,
    magicBricksParamRows,
    magicBricksSuccessResponse,
    magicBricksErrorResponses,
  ]);

  const localApiHint = myOperatorWebhookBase.startsWith("http://")
    ? myOperatorWebhookBase
    : "";

  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      openSuccessNotification(`${label} copied`);
    } catch (_) {
      openErrorNotification("Could not copy — select and copy manually");
    }
  };

  return (
    <Box
      sx={{
        height: "calc(100vh - 68px)",
        overflow: "auto",
        p: 3,
        boxSizing: "border-box",
      }}
    >
      <Box sx={{ maxWidth: 1040, pb: 6 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
        Organization Integrations
      </Typography>

      <FormControl fullWidth size="small" sx={{ mb: 3, maxWidth: 360 }}>
        <InputLabel>Organization</InputLabel>
        <Select
          label="Organization"
          value={
            orgs.some((o) => String(o._id) === String(organizationId))
              ? organizationId
              : ""
          }
          onChange={(e) => setOrganizationId(String(e.target.value))}
          displayEmpty
        >
          {!orgs.length && (
            <MenuItem value="">
              <em>Loading organizations…</em>
            </MenuItem>
          )}
          {orgs.map((o) => (
            <MenuItem key={o._id} value={o._id}>
              {o.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {!organizationId && (
        <Typography color="text.secondary">
          Select an organization (run migration if list is empty).
        </Typography>
      )}

      {organizationId && (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                mb: 2,
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  width: 148,
                  height: 72,
                  borderRadius: 2,
                  bgcolor: "#FFFFFF",
                  border: "1px solid #EEF0F5",
                  boxShadow: "0 1px 2px rgba(15,23,42,0.04)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  px: 1.25,
                  py: 1,
                  overflow: "hidden",
                }}
              >
                <Box
                  component="img"
                  src={engageLogo}
                  alt="Engage Marketing Platform"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                    bgcolor: "#FFFFFF",
                  }}
                />
              </Box>
              <Box sx={{ minWidth: 0, pt: 0.5 }}>
                <Typography variant="h6" sx={{ mb: 0.5, lineHeight: 1.3 }}>
                  Engage
                </Typography>
                <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
                  Connect this organization&apos;s Engage credentials for
                  WhatsApp templates and messaging. Saved values show as
                  ****last4 — paste a new value only when replacing.
                </Typography>
              </Box>
            </Box>
            <TextField
              fullWidth
              size="small"
              label={
                authkeyConfigured
                  ? "Engage key (saved — last 4 shown)"
                  : "Engage key"
              }
              value={authkey}
              onChange={(e) => setAuthkey(e.target.value)}
              onFocus={() => {
                if (looksMasked(authkey)) setAuthkey("");
              }}
              sx={{ mb: 2 }}
              disabled={!!shareFrom}
              placeholder={
                authkeyConfigured
                  ? "Enter new Engage key to replace"
                  : "Paste Engage key…"
              }
              helperText={
                authkeyConfigured && looksMasked(authkey)
                  ? "Masked saved key. Leave as-is to keep, or type a new key to replace."
                  : undefined
              }
            />
            {isSuperAdmin && (
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Use credentials from</InputLabel>
                <Select
                  label="Use credentials from"
                  value={shareFrom}
                  onChange={(e) => setShareFrom(String(e.target.value))}
                >
                  <MenuItem value="">— Own key —</MenuItem>
                  {otherOrgs.map((o) => (
                    <MenuItem key={o._id} value={o._id}>
                      {o.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                onClick={saveAuthKey}
                sx={{ background: "#A02448" }}
              >
                Save Engage
              </Button>
              <Button
                variant="outlined"
                onClick={loadTemplates}
                disabled={loadingTemplates}
              >
                {loadingTemplates ? "Fetching…" : "Fetch templates"}
              </Button>
            </Box>

            {templateOptions.length > 0 && (
              <Typography sx={{ mt: 1.5, fontSize: 13, color: "#059669" }}>
                {templateOptions.length} WhatsApp templates loaded for this org
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontWeight: 500, mb: 1 }}>
              OTP template (wid)
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <FormControl size="small" sx={{ minWidth: 320 }}>
                <InputLabel>OTP template</InputLabel>
                <Select
                  label="OTP template"
                  value={otpWid}
                  onChange={(e) => setOtpWid(String(e.target.value))}
                >
                  <MenuItem value="">
                    <em>— None —</em>
                  </MenuItem>
                  {templateOptions.length === 0 && otpWid && (
                    <MenuItem value={otpWid}>{otpWid}</MenuItem>
                  )}
                  {templateOptions.map((t: any) => {
                    const wid = String(t.wid || t.id || "");
                    return (
                      <MenuItem key={wid} value={wid}>
                        {templateLabel(t)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <Button variant="outlined" onClick={saveOtpTemplate}>
                Save OTP template
              </Button>
              {templateOptions.length === 0 && (
                <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>
                  Fetch templates first
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontWeight: 500, mb: 0.5 }}>
              New lead assigned (staff + admin)
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1.5 }}>
              Internal alert — not sent to the customer. When a website or CRM
              lead is assigned, WhatsApp goes to the project&apos;s assigned
              staff (their WhatsApp number) and to the admin number below.
              Create template <strong>staff_new_lead_assigned</strong> in
              Engage, Fetch templates, then pick it here.
            </Typography>
            <Box
              sx={{
                mb: 1.5,
                p: 1.5,
                borderRadius: 1,
                bgcolor: "#F8FAFC",
                border: "1px solid #E5E7EB",
                fontSize: 12,
                color: "#374151",
                lineHeight: 1.55,
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                Engage → Create Template (Utility / Text / English)
              </Typography>
              <Typography sx={{ fontSize: 12, mb: 0.5 }}>
                Header: <code>New Lead Assigned</code>
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  mb: 1,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: "#111827",
                  color: "#F9FAFB",
                  fontSize: 11,
                  whiteSpace: "pre-wrap",
                  overflowX: "auto",
                }}
              >
                {`Hi {{1}}, a new lead has been assigned to you.

Name: {{2}}
Lead id: {{3}}
Requirement: {{4}}
Source: {{5}}
Time: {{6}}

Please contact within 15 minutes.`}
              </Box>
              <Typography sx={{ fontSize: 12, mb: 0.5 }}>
                Samples: {"{{1}}"} Reeta · {"{{2}}"} Rahul Sharma · {"{{3}}"}{" "}
                LD3102 · {"{{4}}"} 2 BHK enquiry · {"{{5}}"} Website · {"{{6}}"}{" "}
                27 Oct 2026, 10:30 AM
              </Typography>
              <Typography sx={{ fontSize: 12 }}>
                Button: Call To Action → Visit website → Dynamic · text{" "}
                <strong>Open Lead</strong> · URL{" "}
                <code>https://pulse.yhataw.com/all-leads-details/{"{{1}}"}</code>{" "}
                · sample <code>6aacc7d35ef59e2b3d6ec426</code>. The button{" "}
                {"{{1}}"} is the Mongo lead id (not the salesperson name in
                body {"{{1}}"}). Pulse also sends body {"{{7}}"} as the same
                id.
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
                alignItems: "center",
                mb: 1,
              }}
            >
              <FormControl size="small" sx={{ minWidth: 320 }}>
                <InputLabel>Assigned-lead template</InputLabel>
                <Select
                  label="Assigned-lead template"
                  value={assignedWid}
                  onChange={(e) => setAssignedWid(String(e.target.value))}
                >
                  <MenuItem value="">
                    <em>— None —</em>
                  </MenuItem>
                  {templateOptions.length === 0 && assignedWid && (
                    <MenuItem value={assignedWid}>{assignedWid}</MenuItem>
                  )}
                  {templateOptions.map((t: any) => {
                    const wid = String(t.wid || t.id || "");
                    return (
                      <MenuItem key={wid} value={wid}>
                        {templateLabel(t)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <TextField
                size="small"
                label="Admin WhatsApp"
                value={assignedAdminWhatsapp}
                onChange={(e) => setAssignedAdminWhatsapp(e.target.value)}
                sx={{ width: 160 }}
                helperText="Always notified"
              />
              <Button
                variant="outlined"
                onClick={saveLeadAssignedTemplate}
                disabled={savingAssigned}
              >
                {savingAssigned ? "Saving…" : "Save assigned template"}
              </Button>
              {templateOptions.length === 0 && (
                <Typography sx={{ fontSize: 12, color: "#9CA3AF" }}>
                  Fetch templates first
                </Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1 }}>
              Variable map: body {"{{1}}"} salesperson · {"{{2}}"} lead name ·{" "}
              {"{{3}}"} lead id (LD…) · {"{{4}}"} requirement · {"{{5}}"} source ·{" "}
              {"{{6}}"} time · body {"{{7}}"} Mongo id · button {"{{1}}"} Mongo
              id (Open Lead URL)
            </Typography>

            <Typography sx={{ fontWeight: 500, mt: 3, mb: 1 }}>
              Stage trigger template
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1.5 }}>
              All Lead Management stages below. Pick one WhatsApp template per
              stage, or leave empty / delete if that stage should not send a
              message.
            </Typography>

            {stageRows.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: "#9CA3AF" }}>
                No lead stages found. Add stages under Lead Management first.
              </Typography>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: "32%" }}>
                      Lead stage
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      WhatsApp template
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 120 }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 140 }} align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stageRows.map((stage) => {
                    const wid = stageTemplateMap[stage.id] || "";
                    const busy = savingStage === stage.id;
                    const hasConfig = !!findSavedRow(stage.id);
                    return (
                      <TableRow key={stage.id} hover>
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: 14,
                              fontWeight: stage.isParent ? 600 : 400,
                            }}
                          >
                            {stage.label}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" fullWidth>
                            <Select
                              displayEmpty
                              value={wid}
                              disabled={busy}
                              onChange={(e) => {
                                const next = String(e.target.value);
                                setStageTemplateMap((prev) => ({
                                  ...prev,
                                  [stage.id]: next,
                                }));
                              }}
                              renderValue={(selected) => {
                                if (!selected) {
                                  return (
                                    <em style={{ color: "#9CA3AF" }}>
                                      No template
                                    </em>
                                  );
                                }
                                const tpl = templateOptions.find(
                                  (t) =>
                                    String(t.wid || t.id) === String(selected)
                                );
                                return tpl
                                  ? templateLabel(tpl)
                                  : String(selected);
                              }}
                            >
                              <MenuItem value="">
                                <em>No template</em>
                              </MenuItem>
                              {templateOptions.length === 0 && wid && (
                                <MenuItem value={wid}>{wid}</MenuItem>
                              )}
                              {templateOptions.map((t: any) => {
                                const id = String(t.wid || t.id || "");
                                return (
                                  <MenuItem key={id} value={id}>
                                    {templateLabel(t)}
                                  </MenuItem>
                                );
                              })}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell>
                          {hasConfig && wid ? (
                            <Chip size="small" label="active" color="success" />
                          ) : (
                            <Chip size="small" label="none" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={busy}
                            onClick={() =>
                              saveOneStageTemplate(
                                stage.id,
                                stageTemplateMap[stage.id] || ""
                              )
                            }
                            sx={{ mr: 0.5 }}
                          >
                            {busy ? "…" : "Save"}
                          </Button>
                          <Tooltip title="Remove template for this stage">
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                disabled={busy || (!hasConfig && !wid)}
                                onClick={() => deleteOneStage(stage.id)}
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            {templateOptions.length === 0 && stageRows.length > 0 && (
              <Typography sx={{ mt: 1, fontSize: 12, color: "#9CA3AF" }}>
                Tip: click Fetch templates so the dropdown lists template names.
              </Typography>
            )}

            {(() => {
              const known = new Set(stageRows.map((s) => s.id));
              const orphans = savedStageTemplates.filter((row) => {
                const key = String(row.stageName || row.stageId || "");
                return key && !known.has(key);
              });
              if (!orphans.length) return null;
              return (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: 13, color: "#B45309", mb: 1 }}>
                    Saved mappings not matching current stage names (delete or
                    re-save under the correct stage):
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      {orphans.map((row: any) => (
                        <TableRow key={row._id}>
                          <TableCell>
                            {row.stageName || row.stageId}
                          </TableCell>
                          <TableCell>{row.templateWid}</TableCell>
                          <TableCell>{row.templateName || "—"}</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                deleteOneStage(
                                  String(row.stageName || row.stageId)
                                )
                              }
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              );
            })()}
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Google Ads — offline conversions
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#6B7280", mb: 1.5 }}>
              Website form fill is <strong>not</strong> sent as a conversion.
              Capture <code>gclid</code> on Yhataw landing pages, then when CRM
              marks the lead <strong>Pipeline (Qualified)</strong> or{" "}
              <strong>Visit Done</strong>, Pulse uploads that event to Google
              Ads so bidding learns serious buyers.
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
                gap: 1.5,
              }}
            >
              <TextField
                size="small"
                label="Customer ID"
                value={gadsCustomerId}
                onChange={(e) => setGadsCustomerId(e.target.value)}
                placeholder="123-456-7890"
              />
              <TextField
                size="small"
                label="MCC / login customer ID"
                value={gadsLoginCustomerId}
                onChange={(e) => setGadsLoginCustomerId(e.target.value)}
              />
              <TextField
                size="small"
                label={
                  gadsDeveloperTokenConfigured
                    ? "Developer token (saved)"
                    : "Developer token"
                }
                value={gadsDeveloperToken}
                onChange={(e) => setGadsDeveloperToken(e.target.value)}
                onFocus={() => {
                  if (looksMasked(gadsDeveloperToken)) setGadsDeveloperToken("");
                }}
              />
              <TextField
                size="small"
                label={
                  gadsRefreshTokenConfigured
                    ? "Refresh token (saved)"
                    : "OAuth refresh token"
                }
                value={gadsRefreshToken}
                onChange={(e) => setGadsRefreshToken(e.target.value)}
                onFocus={() => {
                  if (looksMasked(gadsRefreshToken)) setGadsRefreshToken("");
                }}
              />
              <TextField
                size="small"
                label="Qualified conversion action ID"
                value={gadsQualifiedId}
                onChange={(e) => setGadsQualifiedId(e.target.value)}
                helperText="CRM stage Pipeline / Prospect"
              />
              <TextField
                size="small"
                label="Site visit conversion action ID"
                value={gadsSiteVisitId}
                onChange={(e) => setGadsSiteVisitId(e.target.value)}
                helperText="CRM stage Visit Done"
              />
              <TextField
                size="small"
                label="Visit planned conversion action ID"
                value={gadsVisitPlannedId}
                onChange={(e) => setGadsVisitPlannedId(e.target.value)}
                helperText="Optional — Visit Planned"
              />
            </Box>
            <Button
              variant="contained"
              onClick={saveGoogleAds}
              disabled={savingGads}
              sx={{ mt: 2, background: "#A02448" }}
            >
              {savingGads ? "Saving…" : "Save Google Ads"}
            </Button>
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                mb: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="h6">Meta Lead Ads (Facebook)</Typography>
              <Chip
                size="small"
                label={metaConnected ? "Connected" : "Not connected"}
                color={metaConnected ? "success" : "default"}
              />
            </Box>
            <Typography sx={{ mb: 2, fontSize: 13, color: "#6B7280" }}>
              Connect Meta → <strong>Refresh from Meta</strong> loads every
              Instant Form from all your Facebook Pages. Map each form to a Pulse
              CRM form — when Meta sends a lead, it is created in All Leads
              automatically.
            </Typography>

            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                bgcolor: "#F8FAFC",
                border: "1px solid #E5E7EB",
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5, color: "#92400E" }}>
                Meta Developer App — fix &quot;domain isn&apos;t included&quot; error
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#374151", mb: 1 }}>
                In{" "}
                <a
                  href="https://developers.facebook.com/apps/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  developers.facebook.com
                </a>{" "}
                → your app → <strong>Settings → Basic</strong>: add{" "}
                <strong>App Domains</strong> (one per line or comma-separated):{" "}
                <code>
                  {(metaWebhook.appDomainsHint || [
                    "yhataw.com",
                    "pulse.yhataw.com",
                    "api.yhataw.com",
                    "localhost",
                  ]).join(", ")}
                </code>
                . Set <strong>Site URL</strong> to{" "}
                <code>{metaWebhook.frontendUrl || "https://pulse.yhataw.com"}</code>.
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5, mt: 1.5, color: "#166534" }}>
                Meta App webhook (required once — real-time leads)
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#374151", mb: 1 }}>
                Yes — add this <strong>once</strong> in{" "}
                <a
                  href="https://developers.facebook.com/apps/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Meta Developer Console
                </a>
                : <strong>Add product → Webhooks</strong> → select{" "}
                <strong>Page</strong> → Callback URL + Verify token below → subscribe to{" "}
                <strong>leadgen</strong>. When you connect a Page in Pulse, the API also
                subscribes that Page to leadgen automatically.
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#374151", mb: 1 }}>
                <strong>Facebook Login → Valid OAuth Redirect URIs</strong> (internal prod):
              </Typography>
              <Typography
                component="code"
                sx={{
                  display: "block",
                  fontSize: 12,
                  wordBreak: "break-all",
                  mb: 1,
                  p: 1,
                  bgcolor: "#FFFBEB",
                  borderRadius: 1,
                  border: "1px solid #FDE68A",
                }}
              >
                {metaWebhook.oauthRedirectUri ||
                  "https://api.yhataw.com/meta/oauth/callback"}
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                Meta webhook callback URL
              </Typography>
              <Typography
                component="code"
                sx={{
                  display: "block",
                  fontSize: 12,
                  wordBreak: "break-all",
                  mb: 1,
                }}
              >
                {metaWebhook.callbackUrl || `${urls.url_prod}/webhook`}
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                Verify token
              </Typography>
              <Typography component="code" sx={{ fontSize: 12 }}>
                {metaWebhook.verifyToken || "facebook_webhook_123"}
              </Typography>
              {metaWebhook.insertLeadsUrl ? (
                <>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, mt: 1.5, mb: 0.5 }}>
                    Make / Zapier push URL
                  </Typography>
                  <Typography
                    component="code"
                    sx={{ display: "block", fontSize: 12, wordBreak: "break-all" }}
                  >
                    POST {metaWebhook.insertLeadsUrl}
                  </Typography>
                </>
              ) : null}
              {metaWebhook.metaLeadgen?.sample ? (
                <Box sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                    Meta leadgen sample payload
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1,
                      maxHeight: 160,
                      overflow: "auto",
                      bgcolor: "#0F172A",
                      color: "#E2E8F0",
                      borderRadius: 1,
                      fontSize: 10,
                    }}
                  >
                    {JSON.stringify(metaWebhook.metaLeadgen.sample, null, 2)}
                  </Box>
                </Box>
              ) : null}
              {metaWebhook.thirdParty?.sample ? (
                <Box sx={{ mt: 1.5 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                    Third-party (insertLeads) sample payload
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1,
                      maxHeight: 160,
                      overflow: "auto",
                      bgcolor: "#0F172A",
                      color: "#E2E8F0",
                      borderRadius: 1,
                      fontSize: 10,
                    }}
                  >
                    {JSON.stringify(metaWebhook.thirdParty.sample, null, 2)}
                  </Box>
                </Box>
              ) : null}
              <Typography sx={{ mt: 1, fontSize: 11, color: "#6B7280" }}>
                For form-specific URL + payload: Lead Forms → click{" "}
                <strong>Webhook</strong> on that form.
              </Typography>
            </Box>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              <Button
                variant={metaConnected ? "outlined" : "contained"}
                color={metaConnected ? "success" : "primary"}
                disabled={metaBusy || metaConnected}
                onClick={connectMetaOAuth}
                startIcon={
                  metaConnected ? (
                    <CheckCircleOutlineIcon fontSize="small" />
                  ) : undefined
                }
              >
                {metaBusy
                  ? "Working…"
                  : metaConnected
                    ? "Connected"
                    : "Connect Meta account"}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                disabled={metaBusy || !metaConnected}
                onClick={() => onRefreshFromMeta(false)}
              >
                Refresh from Meta
              </Button>
              <Button
                variant="text"
                color="error"
                disabled={metaBusy || !metaConnected}
                onClick={onDisconnectMeta}
              >
                Disconnect
              </Button>
              <Button
                variant="text"
                onClick={() => history.push(urls.facebookAds)}
              >
                Open Facebook Forms
              </Button>
            </Box>

            {metaConnected ? (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2, alignItems: "center" }}>
                <Button
                  variant="outlined"
                  disabled={metaBusy}
                  onClick={onLinkAllPages}
                >
                  Link unowned Pages for this org
                </Button>
                {metaForms.length ? (
                  <Chip
                    size="small"
                    color={mappedFormsCount === metaForms.length ? "success" : "default"}
                    label={`${mappedFormsCount}/${metaForms.length} mapped`}
                  />
                ) : null}
              </Box>
            ) : null}

            {metaForms.length ? (
              <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1 }}>
                Showing {metaForms.length} Meta form(s) for this organization
                only. Map each row to a Pulse CRM form from the same org —
                shared Meta accounts must not map Yhataw forms to Home Tatva
                (or vice versa).
              </Typography>
            ) : null}

            {metaConnectedPages?.length ? (
              <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1 }}>
                Webhook pages linked:{" "}
                {metaConnectedPages
                  .map((p: any) => p.pageName || p.pageId)
                  .join(", ")}
              </Typography>
            ) : null}

            {metaForms.length ? (
              <>
                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
                  Meta lead forms — map to Pulse CRM forms
                </Typography>
                <Table size="small" sx={{ mb: 2 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Page</TableCell>
                      <TableCell>Meta form</TableCell>
                      <TableCell>Form id</TableCell>
                      <TableCell>Map to Pulse CRM form</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {metaForms.map((f: any) => (
                      <TableRow key={f.form_id || f._id}>
                        <TableCell>{f.pageName || f.pageId || "—"}</TableCell>
                        <TableCell>{f.form_name || "—"}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 11, fontFamily: "monospace" }}>
                            {f.form_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Select
                            size="small"
                            displayEmpty
                            value={f.map_form_id || ""}
                            onChange={(e) =>
                              onMapForm(String(f.form_id), String(e.target.value))
                            }
                            sx={{ minWidth: 240 }}
                          >
                            <MenuItem value="">
                              <em>
                                {f.map_form_id
                                  ? "Unmap form…"
                                  : "Select CRM form…"}
                              </em>
                            </MenuItem>
                            {orgCrmForms.map((c) => (
                              <MenuItem key={c.id} value={c.id}>
                                {c.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </TableCell>
                        <TableCell>
                          {f.map_form_id ? (
                            <Chip size="small" color="success" label="Mapped" />
                          ) : (
                            <Chip size="small" label="Unmapped" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            ) : metaConnected ? (
              <Typography sx={{ fontSize: 13, color: "#9CA3AF", mb: 2 }}>
                No Meta forms yet — click <strong>Refresh from Meta</strong> after
                connecting. All Instant Forms from your Facebook Pages will appear
                here for mapping.
              </Typography>
            ) : null}

            <Typography sx={{ mt: 2, fontSize: 12, color: "#6B7280" }}>
              Leads route by Meta <strong>form_id</strong> into this organization.
              Map every form before going live. One Meta app webhook (
              <code>/webhook</code>) handles all orgs — page tokens are stored when
              you connect Meta or link Pages.
            </Typography>
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Payment Gateway (Easebuzz)
            </Typography>
            <Typography sx={{ mb: 2, fontSize: 13, color: "#6B7280" }}>
              Key and Salt for this organization&apos;s website payments.
              Saved values show as ****last4 — paste a new value only when
              replacing.
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-start" }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="easebuzz-env-label">Mode</InputLabel>
                <Select
                  labelId="easebuzz-env-label"
                  label="Mode"
                  value={easebuzzEnv}
                  onChange={(e) =>
                    setEasebuzzEnv(e.target.value as "test" | "prod")
                  }
                >
                  <MenuItem value="test">Test / Sandbox</MenuItem>
                  <MenuItem value="prod">Live / Production</MenuItem>
                </Select>
              </FormControl>
              <TextField
                size="small"
                label={
                  easebuzzKeyConfigured ? "Key (****last4)" : "Key"
                }
                value={easebuzzKey}
                onChange={(e) => setEasebuzzKey(e.target.value)}
                onFocus={() => {
                  if (looksMasked(easebuzzKey)) setEasebuzzKey("");
                }}
                placeholder={
                  easebuzzKeyConfigured
                    ? "Enter new key to replace"
                    : "e.g. K51KFFW4Y"
                }
                sx={{ minWidth: 220 }}
                helperText={
                  easebuzzKeyConfigured && looksMasked(easebuzzKey)
                    ? "Masked saved key. Leave as-is to keep."
                    : undefined
                }
              />
              <TextField
                size="small"
                label={
                  easebuzzSaltConfigured ? "Salt (****last4)" : "Salt"
                }
                value={easebuzzSalt}
                onChange={(e) => setEasebuzzSalt(e.target.value)}
                onFocus={() => {
                  if (looksMasked(easebuzzSalt)) setEasebuzzSalt("");
                }}
                placeholder={
                  easebuzzSaltConfigured
                    ? "Enter new salt to replace"
                    : "e.g. 89N0SBA71"
                }
                sx={{ minWidth: 220 }}
                helperText={
                  easebuzzSaltConfigured && looksMasked(easebuzzSalt)
                    ? "Masked saved salt. Leave as-is to keep."
                    : undefined
                }
              />
              <FormControlLabel
                sx={{ mt: 0.2 }}
                control={
                  <Switch
                    checked={easebuzzAmountOverride}
                    onChange={(e) =>
                      setEasebuzzAmountOverride(e.target.checked)
                    }
                  />
                }
                label="Override amount"
              />
              {easebuzzAmountOverride ? (
                <TextField
                  size="small"
                  type="number"
                  label="Override amount (₹)"
                  value={easebuzzDefaultAmount}
                  onChange={(e) => setEasebuzzDefaultAmount(e.target.value)}
                  placeholder="1"
                  sx={{ minWidth: 240 }}
                  helperText="Charged for every payment while this is on — use it to test a live gateway with a token amount. Turn it off to charge the website's own pricing."
                />
              ) : null}
              <Button variant="contained" onClick={saveEasebuzz} sx={{ mt: 0.2 }}>
                Save
              </Button>
            </Box>
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Outgoing Email (SMTP)
            </Typography>
            <Typography sx={{ mb: 2, fontSize: 13, color: "#6B7280" }}>
              Send payment links and other emails from this
              organization&apos;s own mailbox instead of the default Yhataw
              sender. Use an <strong>app password</strong>, not the account
              login password (Gmail: Google Account → Security → App
              passwords). Host and port are detected automatically for Gmail,
              Outlook and Zoho. Leave <strong>Sender email</strong> blank to
              send as the login mailbox. If this is left blank or fails, the
              default sender is used as before.
            </Typography>

            <FormControlLabel
              sx={{ mb: 1 }}
              control={
                <Switch
                  checked={smtpEnabled}
                  onChange={(e) => setSmtpEnabled(e.target.checked)}
                />
              }
              label={
                smtpEnabled
                  ? "Use this organization's email account"
                  : "Disabled — using default sender"
              }
            />

            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
                alignItems: "flex-start",
              }}
            >
              <TextField
                size="small"
                label="Login email (SMTP username)"
                value={smtpUser}
                onChange={(e) => setSmtpUser(e.target.value)}
                placeholder="accounts@yourcompany.com"
                sx={{ minWidth: 260 }}
                helperText="Mailbox the app password belongs to"
              />
              <TextField
                size="small"
                label="Sender email (optional)"
                value={smtpFromEmail}
                onChange={(e) => setSmtpFromEmail(e.target.value)}
                placeholder={smtpUser.trim() || "same as login email"}
                sx={{ minWidth: 260 }}
                helperText="Address recipients see — must be a verified alias"
              />
              <TextField
                size="small"
                type={looksMasked(smtpPassword) ? "text" : "password"}
                label={
                  smtpPasswordConfigured
                    ? "App password (****last4)"
                    : "App password"
                }
                value={smtpPassword}
                onChange={(e) => setSmtpPassword(e.target.value)}
                onFocus={() => {
                  if (looksMasked(smtpPassword)) setSmtpPassword("");
                }}
                placeholder={
                  smtpPasswordConfigured
                    ? "Enter new password to replace"
                    : "16-character app password"
                }
                sx={{ minWidth: 240 }}
                helperText={
                  smtpPasswordConfigured && looksMasked(smtpPassword)
                    ? "Masked saved password. Leave as-is to keep."
                    : undefined
                }
              />
              <TextField
                size="small"
                label="From name"
                value={smtpFromName}
                onChange={(e) => setSmtpFromName(e.target.value)}
                placeholder="e.g. VKS Builders"
                sx={{ minWidth: 200 }}
                helperText="Shown as the sender name"
              />
              <TextField
                size="small"
                label="Reply-to (optional)"
                value={smtpReplyTo}
                onChange={(e) => setSmtpReplyTo(e.target.value)}
                placeholder="sales@yourcompany.com"
                sx={{ minWidth: 220 }}
              />
              <TextField
                size="small"
                label="SMTP host (optional)"
                value={smtpHost}
                onChange={(e) => setSmtpHost(e.target.value)}
                placeholder="auto-detected"
                sx={{ minWidth: 200 }}
              />
              <TextField
                size="small"
                label="Port (optional)"
                value={smtpPort}
                onChange={(e) => setSmtpPort(e.target.value)}
                placeholder="465 or 587"
                sx={{ minWidth: 130 }}
              />
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                flexWrap: "wrap",
                alignItems: "center",
                mt: 2,
              }}
            >
              <Button variant="contained" onClick={saveSmtp}>
                Save
              </Button>
              <TextField
                size="small"
                label="Send test email to (optional)"
                value={smtpTestTo}
                onChange={(e) => setSmtpTestTo(e.target.value)}
                placeholder="you@yourcompany.com"
                sx={{ minWidth: 260 }}
              />
              <Button
                variant="outlined"
                onClick={testSmtp}
                disabled={smtpTesting}
              >
                {smtpTesting ? "Testing…" : "Test connection"}
              </Button>
            </Box>

            {smtpTestResult ? (
              <Typography
                sx={{ mt: 1.5, fontSize: 13, color: "#374151" }}
              >
                {smtpTestResult}
              </Typography>
            ) : null}
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              MyOperator (IVR / Click-to-Call)
            </Typography>
            <Typography sx={{ mb: 2, fontSize: 13, color: "#6B7280" }}>
              OBD click-to-call: Calling APIs → Endpoint Details (Company ID,
              x-api-key, Secret Key) + Public IVR ID + Caller ID. Users / call
              logs / recordings: Manage → API integration →{" "}
              <strong>Public API token</strong> (same token as{" "}
              <a
                href="https://myoperator.com/api"
                target="_blank"
                rel="noreferrer"
              >
                developers.myoperator.co
              </a>{" "}
              <code>/user</code>, <code>/search</code>,{" "}
              <code>/recordings/link</code>).
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
                mb: 2,
              }}
            >
              <TextField
                size="small"
                label="Company ID"
                value={moCompanyId}
                onChange={(e) => setMoCompanyId(e.target.value)}
                helperText="Endpoint Details → Company ID"
              />
              <TextField
                size="small"
                label={
                  moXApiKeyConfigured
                    ? "x-api-key (****last4)"
                    : "x-api-key"
                }
                value={moXApiKey}
                onChange={(e) => setMoXApiKey(e.target.value)}
                onFocus={() => {
                  if (looksMasked(moXApiKey)) setMoXApiKey("");
                }}
                helperText="Endpoint Details → x-api-key"
              />
              <TextField
                size="small"
                label={
                  moSecretKeyConfigured
                    ? "Secret Key (****last4)"
                    : "Secret Key"
                }
                value={moSecretKey}
                onChange={(e) => setMoSecretKey(e.target.value)}
                onFocus={() => {
                  if (looksMasked(moSecretKey)) setMoSecretKey("");
                }}
                helperText="Endpoint Details → Secret Key (header)"
              />
              <TextField
                size="small"
                label={
                  moCallingTokenConfigured
                    ? "Public API token (****last4)"
                    : "Public API token"
                }
                value={moCallingToken}
                onChange={(e) => setMoCallingToken(e.target.value)}
                onFocus={() => {
                  if (looksMasked(moCallingToken)) setMoCallingToken("");
                }}
                helperText="Manage → API integration → Public API token — used for Users, Search logs, Recordings"
              />
              <TextField
                size="small"
                label={
                  moSecretTokenConfigured
                    ? "OBD secret_token (****last4)"
                    : "OBD secret_token"
                }
                value={moSecretToken}
                onChange={(e) => setMoSecretToken(e.target.value)}
                onFocus={() => {
                  if (looksMasked(moSecretToken)) setMoSecretToken("");
                }}
                helperText="Auto-uses Secret Key above on save (do not paste Authentication here)"
              />
              <TextField
                size="small"
                label="Public IVR ID"
                value={moPublicIvrId}
                onChange={(e) => setMoPublicIvrId(e.target.value)}
                placeholder="e.g. 68257f8691860690"
                helperText="Call → Outgoing → Campaign → Peer to peer → Public IVR ID (hex id, NOT a phone number)"
              />
              <TextField
                size="small"
                label="Caller ID (DID)"
                value={moCallerId}
                onChange={(e) => setMoCallerId(e.target.value)}
                placeholder="1204884288"
                helperText="Virtual number digits only, e.g. 1204884288 (not 91…)"
              />
              <TextField
                size="small"
                label={
                  moWebhookSecretConfigured
                    ? "Webhook API key (****last4)"
                    : "Webhook API key (optional — leave blank)"
                }
                value={moWebhookSecret}
                onChange={(e) => setMoWebhookSecret(e.target.value)}
                onFocus={() => {
                  if (looksMasked(moWebhookSecret)) setMoWebhookSecret("");
                }}
                helperText="Not required. Only if Webhooks v2 Authentication = API Key — then paste the same key here"
              />
            </Box>
            <Button
              variant="contained"
              onClick={saveMyOperator}
              sx={{ background: "#A02448", mb: 2 }}
            >
              Save MyOperator
            </Button>
            <Box
              sx={{
                p: 2,
                bgcolor: "#F8FAFC",
                borderRadius: 1,
                border: "1px solid #E5E7EB",
              }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                Why Public IVR ID?
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1.5 }}>
                Must be an <strong>Enabled</strong> Peer-to-peer campaign with your
                DID linked. Your <code>Yhataw P2P</code> (
                <code>6a8c33b0cd445709</code>) currently shows{" "}
                <strong>Disabled</strong> and <strong>Linked DID 0</strong> — MyOperator
                then returns “Public ID or Company id is invalid”. In MyOperator: Call →
                Outgoing → Campaign → open campaign → Enable → link DID{" "}
                <code>1204884288</code> → Save. Drafted campaigns also fail until started.
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>
                Why Webhook API key?
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1.5 }}>
                <strong>Skip it.</strong> In Add New Webhook set Authentication
                = <strong>None</strong>. The key field in Pulse is only if you
                later choose API Key auth on MyOperator and want Pulse to verify
                the same secret.
              </Typography>

              <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>
                MyOperator → Webhooks v2 → Add New Webhook
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1 }}>
                MyOperator accepts <strong>HTTPS only</strong> (localhost HTTP
                is rejected). Use your live API host below.
                {localApiHint ? (
                  <>
                    {" "}
                    Local CRM talks to <code>{localApiHint}</code>; webhooks
                    must hit the public HTTPS API so MyOperator can reach it.
                  </>
                ) : null}
              </Typography>

              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                1) Webhook URL * (paste this)
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 1.5,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  component="code"
                  sx={{
                    flex: 1,
                    minWidth: 200,
                    fontSize: 12,
                    wordBreak: "break-all",
                    p: 1,
                    bgcolor: "#0F172A",
                    color: "#E2E8F0",
                    borderRadius: 1,
                  }}
                >
                  {myOperatorWebhookV2Url}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    copyText(myOperatorWebhookV2Url, "Webhook URL")
                  }
                >
                  Copy URL
                </Button>
              </Box>

              <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1.5 }}>
                2) Authentication → <strong>None</strong>
                <br />
                3) Additional Headers → leave empty
                <br />
                4) Under <strong>Call events</strong>, select all of these:
              </Typography>
              <Box
                component="ul"
                sx={{ m: 0, mb: 1.5, pl: 2.5, fontSize: 12, color: "#374151" }}
              >
                <li>Start of the call → call.initiated</li>
                <li>Start Dialing → call.dial_begin</li>
                <li>Call Responded → call.answered</li>
                <li>Call Ended → call.end</li>
                <li>Call Summary → call.summary (recordings + agent details)</li>
                <li>Call Disposition → optional</li>
              </Box>
              <Typography sx={{ fontSize: 12, color: "#6B7280", mb: 1.5 }}>
                WhatsApp events → leave unchecked (not needed for IVR).
                Then click <strong>Save Webhook</strong>.
              </Typography>

              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                Optional 2nd webhook (legacy after-call / recordings backup)
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 1,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  component="code"
                  sx={{
                    flex: 1,
                    minWidth: 200,
                    fontSize: 12,
                    wordBreak: "break-all",
                    p: 1,
                    bgcolor: "#F3F4F6",
                    borderRadius: 1,
                  }}
                >
                  {myOperatorAfterCallUrl}
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={() =>
                    copyText(myOperatorAfterCallUrl, "After-call URL")
                  }
                >
                  Copy
                </Button>
              </Box>
              <Typography sx={{ fontSize: 11, color: "#9CA3AF", mb: 1 }}>
                Prefer Webhooks v2 with Call Summary selected. After-call is a
                backup if your panel still uses Webhooks v1 after-call.
              </Typography>

              <Typography sx={{ fontSize: 11, color: "#6B7280" }}>
                Next: Team → IVR Mapping (map each agent to a MyOperator user).
                Live duration appears on Team + Admin dashboard; recordings on
                Reports → Call Logs.{" "}
                <Button
                  size="small"
                  sx={{ textTransform: "none", p: 0, minWidth: 0 }}
                  onClick={() => history.push(urls.teamViewPath)}
                >
                  Open Team
                </Button>
                {" · "}
                <Button
                  size="small"
                  sx={{ textTransform: "none", p: 0, minWidth: 0 }}
                  onClick={() => history.push(urls.callLogs)}
                >
                  Call Logs
                </Button>
              </Typography>
            </Box>
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Housing
            </Typography>
            <Typography sx={{ mb: 2, fontSize: 13, color: "#6B7280" }}>
              Credentials used when pulling Housing.com builder leads for this
              organization.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
              <TextField
                size="small"
                label={
                  housingSecretConfigured
                    ? "Secret key (****last4)"
                    : "Secret key"
                }
                value={housingSecret}
                onChange={(e) => setHousingSecret(e.target.value)}
                onFocus={() => {
                  if (looksMasked(housingSecret)) setHousingSecret("");
                }}
                sx={{ minWidth: 220 }}
                placeholder={
                  housingSecretConfigured
                    ? "Enter new key to replace"
                    : undefined
                }
              />
              <TextField
                size="small"
                label="Builder id"
                value={housingId}
                onChange={(e) => setHousingId(e.target.value)}
                sx={{ minWidth: 160 }}
              />
              <Button variant="outlined" onClick={saveHousing}>
                Save
              </Button>
            </Box>
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              99acres
            </Typography>
            <Typography sx={{ mb: 2, fontSize: 13, color: "#6B7280" }}>
              Login used by the 99acres lead pull for this organization.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <TextField
                size="small"
                label="Username"
                value={acresUser}
                onChange={(e) => setAcresUser(e.target.value)}
              />
              <TextField
                size="small"
                label={
                  acresPassConfigured ? "Password (****last4)" : "Password"
                }
                type={looksMasked(acresPass) ? "text" : "password"}
                value={acresPass}
                onChange={(e) => setAcresPass(e.target.value)}
                onFocus={() => {
                  if (looksMasked(acresPass)) setAcresPass("");
                }}
                placeholder={
                  acresPassConfigured ? "Enter new password to replace" : undefined
                }
              />
              <Button variant="outlined" onClick={saveAcres}>
                Save
              </Button>
            </Box>
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              MagicBricks
            </Typography>
            <Typography sx={{ mb: 2, fontSize: 13, color: "#6B7280" }}>
              MagicBricks does <strong>not</strong> use login credentials like
              Housing or 99acres. You generate an <strong>API key</strong> here;
              MagicBricks <strong>pushes</strong> each lead to your webhook.
              Projects in{" "}
              <Button
                size="small"
                variant="text"
                sx={{ p: 0, minWidth: 0, verticalAlign: "baseline", textTransform: "none" }}
                onClick={() => history.push(urls.aggregatorApi)}
              >
                Aggregator API
              </Button>{" "}
              appear automatically when a lead arrives with{" "}
              <code>project</code> / <code>project_id</code> (e.g.{" "}
              <code>MB_995921</code>) — nothing is fetched from MagicBricks.
            </Typography>

            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                bgcolor: mbApiKeyConfigured ? "#F0FDF4" : "#FFF7ED",
                border: `1px solid ${mbApiKeyConfigured ? "#BBF7D0" : "#FED7AA"}`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  Your credentials (per organization)
                </Typography>
                <Chip
                  size="small"
                  label={
                    mbApiKeyConfigured
                      ? "API key saved"
                      : "Not configured — generate & save"
                  }
                  color={mbApiKeyConfigured ? "success" : "warning"}
                  variant="outlined"
                />
              </Box>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1 }}>
                <TextField
                  size="small"
                  label="Partner / Builder id (optional)"
                  value={mbPartnerId}
                  onChange={(e) => setMbPartnerId(e.target.value)}
                  helperText="Your MagicBricks builder account id, for your records"
                />
                <TextField
                  size="small"
                  label={mbApiKeyConfigured ? "API key (****last4)" : "API key"}
                  value={mbApiKey}
                  onChange={(e) => setMbApiKey(e.target.value)}
                  onFocus={() => {
                    if (looksMasked(mbApiKey)) setMbApiKey("");
                  }}
                  placeholder={
                    mbApiKeyConfigured
                      ? "Enter new key to replace saved key"
                      : "Click Generate key, then Save"
                  }
                  sx={{ minWidth: 300 }}
                />
                <Button variant="outlined" onClick={generateMagicBricksApiKey}>
                  Generate key
                </Button>
                <Button variant="contained" onClick={saveMagicBricks}>
                  Save credentials
                </Button>
              </Box>
              {mbApiKeyConfigured ? (
                <Typography sx={{ fontSize: 12, color: "#166534" }}>
                  Saved API key: <code>{mbApiKey}</code>
                  {mbPartnerId ? (
                    <>
                      {" "}
                      · Partner id: <code>{mbPartnerId}</code>
                    </>
                  ) : null}
                  . Copy the full key before saving if MagicBricks needs it —
                  after save only last 4 digits are shown.
                </Typography>
              ) : (
                <Typography sx={{ fontSize: 12, color: "#9A3412" }}>
                  No API key saved for this organization yet. Generate a key,
                  click <strong>Save credentials</strong>, then share the webhook
                  URL + key with Kapil Arora (kapil.arora@magicbricks.com).
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 1,
                bgcolor: "#EFF6FF",
                border: "1px solid #BFDBFE",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                  mb: 1.5,
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1E3A8A" }}>
                  MagicBricks integration sheet
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() =>
                      copyText(
                        magicBricksIntegrationSheet,
                        "Full MagicBricks integration sheet"
                      )
                    }
                  >
                    Copy full sheet (email to MagicBricks)
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() =>
                      copyText(magicBricksSampleCurl, "Sample curl command")
                    }
                  >
                    Copy sample curl
                  </Button>
                </Box>
              </Box>
              <Typography sx={{ fontSize: 12, color: "#374151", mb: 1.5 }}>
                Share this table with the MagicBricks team — values update per
                organization after you save credentials.
              </Typography>
              <Table
                size="small"
                sx={{
                  mb: 2,
                  bgcolor: "#fff",
                  "& td:first-of-type": {
                    fontWeight: 600,
                    width: "32%",
                    verticalAlign: "top",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableBody>
                  {[
                    ["CRM Name", `YHATAW Pulse CRM (${selectedOrgName})`],
                    ["Push Integration Type", "Push — Real-time webhook (HTTP POST JSON)"],
                    ["Authentication Type", "API Key (HTTP header: API-Key)"],
                    ["API Key", magicBricksApiKeyDisplay],
                    ["Client_Key", magicBricksApiKeyDisplay],
                    [
                      "Client_ID",
                      magicBricksClientId ||
                        "Set Partner / Builder id above (or org id is used)",
                    ],
                    [
                      "Working Endpoint Open URL",
                      `POST ${magicBricksWebhookUrl}`,
                    ],
                    [
                      "Complete Working Endpoint URL",
                      `POST ${magicBricksWebhookUrl} + Header API-Key + JSON body (see sample curl)`,
                    ],
                    [
                      "Response Message on lead sharing (success)",
                      '{ "success": 1, "message": "Data Inserted successfully — map once in Aggregator API", ... }',
                    ],
                    [
                      "Response on failure",
                      '{ "success": 0, "message": "Invalid API-Key..." } or missing name/mobile',
                    ],
                  ].map(([label, value]) => (
                    <TableRow key={label}>
                      <TableCell>{label}</TableCell>
                      <TableCell sx={{ fontSize: 12, wordBreak: "break-all" }}>
                        {String(label).includes("URL") ||
                        String(label).includes("Endpoint") ? (
                          <code>{value}</code>
                        ) : (
                          value
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                Parameters to be passed (with sample values)
              </Typography>
              <Table size="small" sx={{ mb: 2, bgcolor: "#fff" }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Parameter</TableCell>
                    <TableCell>Required</TableCell>
                    <TableCell>Sample value</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {magicBricksParamRows.map(([field, req, sample, desc]) => (
                    <TableRow key={field}>
                      <TableCell>
                        <code>{field}</code>
                      </TableCell>
                      <TableCell>{req}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        <code>{sample}</code>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{desc}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                Integration manual
              </Typography>
              <Typography
                component="ol"
                sx={{
                  fontSize: 12,
                  color: "#374151",
                  pl: 2.5,
                  m: 0,
                  "& li": { mb: 0.75 },
                }}
              >
                <li>
                  MagicBricks <strong>pushes</strong> leads — YHATAW does not pull
                  or login to MagicBricks.
                </li>
                <li>
                  Configure <strong>POST</strong> to{" "}
                  <code>{magicBricksWebhookUrl}</code>
                </li>
                <li>
                  Every request must include header{" "}
                  <code>API-Key: &lt;your org key&gt;</code> and{" "}
                  <code>Content-Type: application/json</code>.
                </li>
                <li>
                  Minimum JSON fields: <code>name</code>, <code>mobile</code>,{" "}
                  <code>project</code> (<code>project_id</code> recommended).
                </li>
                <li>
                  Success response <code>success: 1</code> — lead queued under portal
                  uid <code>MB_&lt;project_id&gt;</code> in Aggregator API.
                </li>
                <li>
                  Builder maps each portal project once in Pulse → Aggregator API;
                  then new leads auto-flow to All Leads.
                </li>
              </Typography>

              <Typography sx={{ fontSize: 12, fontWeight: 600, mt: 1.5, mb: 0.5 }}>
                Sample success response (HTTP 200)
              </Typography>
              <Typography
                component="pre"
                sx={{
                  fontSize: 11,
                  p: 1,
                  bgcolor: "#fff",
                  borderRadius: 1,
                  border: "1px solid #E5E7EB",
                  overflow: "auto",
                  maxHeight: 160,
                  m: 0,
                }}
              >
                {magicBricksSuccessResponse}
              </Typography>
            </Box>

            <Box
              sx={{
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                bgcolor: "#F8FAFC",
                border: "1px solid #E5E7EB",
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                Webhook URL (give this to MagicBricks)
              </Typography>
              <Typography
                component="code"
                sx={{
                  display: "block",
                  fontSize: 12,
                  wordBreak: "break-all",
                  mb: 1,
                }}
              >
                POST {magicBricksWebhookUrl}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  copyText(magicBricksWebhookUrl, "MagicBricks webhook URL")
                }
                sx={{ mr: 1, mb: 1 }}
              >
                Copy URL
              </Button>
              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                Header
              </Typography>
              <Typography component="code" sx={{ fontSize: 12, display: "block", mb: 1 }}>
                API-Key:{" "}
                {mbApiKeyConfigured
                  ? mbApiKey
                  : "<save your org API key above first>"}
              </Typography>
              {mbApiKeyConfigured && (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mb: 1 }}
                  onClick={() =>
                    copyText(
                      `API-Key: ${mbApiKey}`,
                      "API-Key header hint (use full key you saved)"
                    )
                  }
                >
                  Copy header hint
                </Button>
              )}
              <Typography sx={{ fontSize: 12, fontWeight: 600, mt: 1.5, mb: 0.5 }}>
                Legacy URL (optional)
              </Typography>
              <Typography
                component="code"
                sx={{ display: "block", fontSize: 12, wordBreak: "break-all" }}
              >
                POST {magicBricksLegacyWebhookUrl}
              </Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 600, mt: 1.5, mb: 0.5 }}>
                JSON body parameters
              </Typography>
              <Table size="small" sx={{ mb: 1 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Field</TableCell>
                    <TableCell>Required</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[
                    ["name", "Yes", "Lead full name"],
                    ["mobile", "Yes", "Phone (10 digits; countryCode default 91)"],
                    ["project", "Yes", "Project name — auto-maps to CRM if name matches yours; else MB_<slug> in Aggregator"],
                    ["project_id", "No", "MagicBricks project id → MB_<id> (portal uid; optional if name matches CRM)"],
                    ["remark", "No", "Extra free-text — stored on lead separately"],
                    ["email", "No", "Lead email"],
                    ["city / state / location", "No", "Location fields"],
                    ["budget / notes", "No", "Budget and comments"],
                    ["property / propertyType / bhkType", "No", "Property hints"],
                    ["additionalProperties", "No", "Object e.g. EnquiredFor, NoOfBHK"],
                  ].map(([field, req, desc]) => (
                    <TableRow key={field}>
                      <TableCell>
                        <code>{field}</code>
                      </TableCell>
                      <TableCell>{req}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{desc}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>
                Sample JSON
              </Typography>
              <Typography
                component="pre"
                sx={{
                  fontSize: 11,
                  p: 1,
                  bgcolor: "#fff",
                  borderRadius: 1,
                  border: "1px solid #E5E7EB",
                  overflow: "auto",
                  maxHeight: 220,
                }}
              >
                {`{
  "name": "Himanshu Kumar",
  "mobile": "9988776655",
  "project": "Aamor",
  "project_id": "12345",
  "remark": "Any extra info from MagicBricks",
  "city": "Jaipur",
  "state": "Rajasthan",
  "notes": "comments",
  "additionalProperties": { "EnquiredFor": "Buy", "NoOfBHK": "3" }
}`}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
                onClick={() =>
                  copyText(
                    `curl --location --request POST '${magicBricksWebhookUrl}' -H 'API-Key: YOUR_ORG_API_KEY' -H 'Content-Type: application/json' -d '{"name":"Test Lead","mobile":"9988776655","project":"Aamor","remark":"test push"}'`,
                    "Sample curl"
                  )
                }
              >
                Copy sample curl
              </Button>
            </Box>
          </Paper>
        </>
      )}
      </Box>
    </Box>
  );
};

export default OrgIntegrationsPage;
